// src/pages/student/StudentAssingments.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Card, Button, Badge, Spinner, Row, Col, Form, Alert, InputGroup, Modal, Dropdown
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, where, setDoc, serverTimestamp
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { db, storage } from "../../services/firebase";

// ⬇️ virtualization
import { FixedSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

/* ---------------- helpers ---------------- */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmtAt = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY, h:mm A") : "—";
};
const fmtDate = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : "—";
};
const uniqBy = (arr, key = (x) => x.id) => {
  const m = new Map();
  for (const x of arr) m.set(key(x), x);
  return [...m.values()];
};
const norm = (s) => String(s || "").trim().toLowerCase();
const TOAST_CID = "assignments-toasts";

const showIndexLink = (err) => {
  const url = err?.message?.match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i)?.[0];
  if (!url) return;
  toast.info(
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      Create required Firestore index
    </a>,
    { autoClose: 9000, closeOnClick: false, containerId: TOAST_CID }
  );
};

/* ------- Theme helpers (same pattern as other pages) ------- */
const ACCENTS = [
  "#0d6efd","#3b82f6","#1d4ed8","#60a5fa",
  "#dc3545","#ef4444","#991b1b","#f87171",
  "#198754","#22c55e","#065f46","#86efac",
  "#ffc107","#facc15","#d97706","#fde047",
  "#6f42c1","#8b5cf6","#7e22ce","#c4b5fd",
  "#20c997","#06b6d4","#164e63","#67e8f9",
  "#fd7e14","#f97316","#9a3412","#fb923c",
  "#6c757d","#94a3b8","#334155","#e2e8f0",
  "#000000","#ffffff",
];
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace(/^#/, "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : "#3b82f6";
};

/* -------- Subject icons -------- */
const SUBJECT_ICONS = {
  physics: "bi-lightning-charge",
  chemistry: "bi-beaker",
  biology: "bi-heart-pulse",
  zoology: "bi-bug",
  botany: "bi-flower3",
  maths: "bi-calculator",
  mathematics: "bi-calculator",
  algebra: "bi-calculator",
  geometry: "bi-rulers",
  statistics: "bi-bar-chart",
  social: "bi-people",
  "social science": "bi-people",
  history: "bi-hourglass-split",
  geography: "bi-geo-alt",
  civics: "bi-bank",
  politics: "bi-bank",
  computers: "bi-pc-display",
  "computer science": "bi-code-slash",
  coding: "bi-code-slash",
  english: "bi-book",
  hindi: "bi-translate",
  sanskrit: "bi-translate",
  tamil: "bi-translate",
  telugu: "bi-translate",
  kannada: "bi-translate",
  marathi: "bi-translate",
  french: "bi-translate",
  german: "bi-translate",
  arts: "bi-palette",
  drawing: "bi-brush",
  music: "bi-music-note-beamed",
  dance: "bi-music-note-list",
  economics: "bi-graph-up",
  commerce: "bi-cash-coin",
  accounts: "bi-journal-richtext",
  science: "bi-magnet",
  robotics: "bi-robot",
  default: "bi-journal-text",
};
const subjectIcon = (subject) => SUBJECT_ICONS[norm(subject)] || SUBJECT_ICONS.default;

/* ---------------- audience matchers (section + branch) ---------------- */
const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const matchesSection = (item, studentSection) => {
  if (!studentSection) return true;
  const s = String(studentSection).trim();
  if (Array.isArray(item.sections) && item.sections.length) {
    return item.sections.some((x) => same(x, s)) || item.sections.some((x) => same(x, "all"));
  }
  if (item.section) {
    return same(item.section, s) || same(item.section, "all");
  }
  return true;
};

const matchesBranch = (item, studentBranch) => {
  const b = String(studentBranch || "").trim();
  if (!b) return true;
  if (Array.isArray(item.branches) && item.branches.length) {
    return item.branches.some((x) => same(x, b)) || item.branches.some((x) => same(x, "all"));
  }
  if (item.branch) {
    return same(item.branch, b) || same(item.branch, "all");
  }
  return true;
};

const matchesAudience = (item, profile) =>
  matchesSection(item, profile?.section) && matchesBranch(item, profile?.branch);

/* ---------------- Theme Picker ---------------- */
function AccentDropdown({ active, onPick, hexToRgba }) {
  const act = normalizeHex6(active);
  return (
    <Dropdown>
      <Dropdown.Menu style={{ padding: 10, maxHeight: 240, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 24px)", gap: 8, minWidth: 230 }}>
          {ACCENTS.map((c) => {
            const col = normalizeHex6(c);
            return (
              <button
                key={col}
                type="button"
                title={col}
                onClick={() => onPick && onPick(col)}
                style={{
                  width: 20, height: 20, borderRadius: "50%", background: col,
                  border: "none", outline: "none", cursor: "pointer",
                  transition: "box-shadow .2s, transform .15s",
                  boxShadow: act === col
                    ? `0 0 0 2px rgba(0,0,0,.35), 0 0 0 4px ${hexToRgba(col, .4)}`
                    : `0 0 0 1px rgba(0,0,0,.2)`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
            );
          })}
          <label
            style={{
              width: 20, height: 20, borderRadius: "50%", cursor: "pointer",
              border: "1px dashed rgba(0,0,0,.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "#666",
            }}
            title="Custom color"
          >
            +
            <input type="color" style={{ display: "none" }} onChange={(e) => onPick && onPick(e.target.value)} />
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/* ---------------- Submit Modal ---------------- */
function SubmitModal({ show, onHide, onSubmit, initialStatus = "completed" }) {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(initialStatus);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setStatus(initialStatus);
    }
  }, [show, initialStatus]);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await onSubmit({ file, status });
      onHide();
    } catch (e) {
      console.error(e);
      toast.error("Failed to submit assignment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Submit / Update Assignment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Status</Form.Label>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
          </Form.Select>
          <Form.Text className="text-muted">
            You can mark as <b>Pending</b> without uploading a file if needed.
          </Form.Text>
        </Form.Group>

        <Form.Group>
          <Form.Label>Upload file (optional)</Form.Label>
          <Form.Control
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*,.doc,.docx,.xlsx,.ppt,.pptx,.zip,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <Form.Text className="text-muted">
            Attach your work. Max size depends on school policy.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button className="btn-neo" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------------- Page ---------------- */
export default function StudentAssingments() {
  const brand = useBrand();
  const { primary, accent, setPrimary, setAccent } = brand || {};
  const primaryColor = useMemo(() => normalizeHex6(primary || "#3b82f6"), [primary]);
  const accentComputed = useMemo(
    () => normalizeHex6(accent || primaryColor),
    [accent, primaryColor]
  );

  // push theme to :root
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", primaryColor);
    root.style.setProperty("--brand-accent", accentComputed);
  }, [primaryColor, accentComputed]);

  const pickTheme = useCallback((c) => {
    const col = normalizeHex6(c);
    setPrimary?.(col);
    setAccent?.(col);
  }, [setPrimary, setAccent]);

  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  const auth = getAuth();
  const uid = auth.currentUser?.uid || null;

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [items, setItems] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);

  const [people, setPeople] = useState({});

  const [submissions, setSubmissions] = useState({});
  const subsUnsubsRef = useRef({});

  const [onlyPending, setOnlyPending] = useState(false);
  const [q, setQ] = useState("");
  const [fSubject, setFSubject] = useState("all");
  const [fTeacher, setFTeacher] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalAssignment, setModalAssignment] = useState(null);

  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  useEffect(() => {
    if (!schoolId || !studentId) {
      toast.error("Missing student session. Please sign in again.", { containerId: TOAST_CID });
      return;
    }
    setLoadingProfile(true);
    const ref = doc(db, "schools", schoolId, "students", studentId);
    getDoc(ref)
      .then((snap) => {
        if (!snap.exists()) {
          setProfile(null);
          toast.error("Could not find your student profile.", { containerId: TOAST_CID });
        } else {
          setProfile({ id: snap.id, ...snap.data() });
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to load profile.", { containerId: TOAST_CID });
      })
      .finally(() => setLoadingProfile(false));
  }, [schoolId, studentId]);

  const isActive = norm(profile?.status) !== "inactive";

  // subscribe to assignments
  useEffect(() => {
    Object.values(subsUnsubsRef.current).forEach((u) => u && u());
    subsUnsubsRef.current = {};
    setSubmissions({});

    if (!schoolId || !profile?.className) return;
    if (!isActive) {
      setItems([]);
      setLoadingAssignments(false);
      return;
    }

    setLoadingAssignments(true);
    const col = collection(db, "schools", schoolId, "assignments");

    const q1 = profile.section
      ? query(
          col,
          where("className", "==", profile.className),
          where("sections", "array-contains", profile.section)
        )
      : null;

    const q2 = profile.section
      ? query(
          col,
          where("className", "==", profile.className),
          where("section", "in", [profile.section, "all"])
        )
      : query(col, where("className", "==", profile.className));

    const unsubs = [];

    if (q1) {
      const u1 = onSnapshot(
        q1,
        (snap) => {
          const A = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setItems((prev) => {
            const merged = uniqBy([...A, ...prev]);
            return merged.sort((a, b) => (toJS(a.givenAt || a.createdAt) - toJS(b.givenAt || b.createdAt)));
          });
          setLoadingAssignments(false);
        },
        (err) => {
          console.error(err);
          showIndexLink(err);
          setLoadingAssignments(false);
        }
      );
      unsubs.push(u1);
    }

    const u2 = onSnapshot(
      q2,
      (snap) => {
        const B = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems((prev) => {
          const merged = uniqBy([...prev, ...B]);
          return merged.sort((a, b) => (toJS(a.givenAt || a.createdAt) - toJS(b.givenAt || b.createdAt)));
        });
        setLoadingAssignments(false);
      },
      (err) => {
        console.error(err);
        showIndexLink(err);
        toast.error("Failed to load assignments.", { containerId: TOAST_CID });
        setLoadingAssignments(false);
      }
    );
    unsubs.push(u2);

    if (profile.branch) {
      try {
        const q3 = query(
          col,
          where("className", "==", profile.className),
          where("branches", "array-contains", profile.branch)
        );
        const u3 = onSnapshot(
          q3,
          (snap) => {
            const C = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setItems((prev) => {
              const merged = uniqBy([...prev, ...C]);
              return merged.sort((a, b) => (toJS(a.givenAt || a.createdAt) - toJS(b.givenAt || b.createdAt)));
            });
          },
          (err) => {
            console.error(err);
            showIndexLink(err);
          }
        );
        unsubs.push(u3);
      } catch {}
    }

    return () => {
      unsubs.forEach((u) => u && u());
      Object.values(subsUnsubsRef.current).forEach((u) => u && u());
      subsUnsubsRef.current = {};
    };
  }, [schoolId, profile?.className, profile?.section, profile?.branch, isActive]);

  // subscribe to student's submission doc per assignment
  useEffect(() => {
    if (!schoolId || !uid || items.length === 0) return;

    const active = subsUnsubsRef.current;

    items.forEach((a) => {
      const subPath = doc(db, "schools", schoolId, "assignments", a.id, "submissions", uid);
      if (active[a.id]) return;
      active[a.id] = onSnapshot(subPath, (snap) => {
        setSubmissions((prev) => ({ ...prev, [a.id]: snap.exists() ? { id: snap.id, ...snap.data() } : null }));
      });
    });

    const ids = new Set(items.map((x) => x.id));
    Object.keys(active).forEach((aid) => {
      if (!ids.has(aid)) {
        active[aid]?.();
        delete active[aid];
        setSubmissions((prev) => {
          const copy = { ...prev }; delete copy[aid]; return copy;
        });
      }
    });
  }, [items, schoolId, uid]);

  // teacher cache
  useEffect(() => {
    if (!schoolId || items.length === 0) return;
    const ids = Array.from(
      new Set(
        items.flatMap((it) => [it.teacherId].filter(Boolean).map((v) => String(v).trim()))
      )
    );
    const missing = ids.filter((id) => !(id in people));
    if (missing.length === 0) return;

    const collections = ["teachers", "staff", "users"];
    const loadOne = async (id) => {
      for (const coll of collections) {
        try {
          const refDoc = doc(db, "schools", schoolId, coll, id);
          const snap = await getDoc(refDoc);
          if (snap.exists()) return { id: snap.id, ...snap.data() };
        } catch {}
      }
      for (const coll of collections) {
        try {
          const q1 = query(collection(db, "schools", schoolId, coll), where("uid", "==", id));
          const qs = await getDocs(q1);
          if (!qs.empty) return { id: qs.docs[0].id, ...qs.docs[0].data() };
        } catch {}
      }
      for (const coll of collections) {
        try {
          const q2 = query(collection(db, "schools", schoolId, coll), where("email", "==", id));
          const qs = await getDocs(q2);
          if (!qs.empty) return { id: qs.docs[0].id, ...qs.docs[0].data() };
        } catch {}
      }
      return null;
    };

    (async () => {
      const entries = await Promise.all(missing.map(async (id) => [id, await loadOne(id)]));
      const next = Object.fromEntries(entries);
      if (Object.keys(next).length) setPeople((p) => ({ ...p, ...next }));
    })();
  }, [schoolId, items, people]);

  // classify
  const classify = (a) => {
    const now = new Date();
    const due = toJS(a.dueAt);
    const sub = submissions[a.id];

    const subStatus = norm(sub?.status);
    const submitted = subStatus === "completed";
    const graded = !!sub?.gradedAt || typeof sub?.score === "number";

    const upcoming = due && now < dayjs(due).startOf("day").toDate();
    const dueSoon = due && !submitted && dayjs(due).diff(now, "hour") <= 48 && dayjs(due).isAfter(now);
    const overdue = due && !submitted && now > due;
    const pending = subStatus === "pending" && !graded;

    return { upcoming, dueSoon, overdue, submitted, graded, pending, due };
  };

  // filter options
  const subjectOptions = useMemo(() => {
    const s = new Set();
    items.forEach((it) => {
      const sub = (it.subject || it.title || "").trim();
      if (sub) s.add(sub);
    });
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [items]);

  const teacherOptions = useMemo(() => {
    const names = new Set();
    items.forEach((a) => {
      const tKey = a.teacherId ? String(a.teacherId).trim() : "";
      const teacher = tKey ? people[tKey] : null;
      const teacherName =
        teacher?.displayName ||
        teacher?.name ||
        [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
        teacher?.fullName ||
        a.teacherName ||
        (typeof a.teacher === "string" ? a.teacher : "") ||
        "";
      if (teacherName) names.add(teacherName);
    });
    return ["all", ...Array.from(names).sort((a, b) => a.localeCompare(b))];
  }, [items, people]);

  // filtered
  const filtered = useMemo(() => {
    const base = items.slice();
    return base
      .filter((a) => matchesAudience(a, profile))
      .filter((a) => {
        const { overdue, submitted, graded, pending, upcoming, dueSoon, due } = classify(a);

        if (onlyPending) {
          if (submitted || graded) return false;
        }

        if (fStatus !== "all") {
          if (fStatus === "upcoming" && !upcoming) return false;
          if (fStatus === "dueSoon" && !dueSoon) return false;
          if (fStatus === "overdue" && !overdue) return false;
          if (fStatus === "submitted" && !submitted) return false;
          if (fStatus === "graded" && !graded) return false;
        }

        if (fSubject !== "all") {
          const sub = (a.subject || a.title || "").trim();
          if (sub !== fSubject) return false;
        }

        if (fTeacher !== "all") {
          const tKey = a.teacherId ? String(a.teacherId).trim() : "";
          const teacher = tKey ? people[tKey] : null;
          const teacherName =
            teacher?.displayName ||
            teacher?.name ||
            [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
            teacher?.fullName ||
            a.teacherName ||
            (typeof a.teacher === "string" ? a.teacher : "") ||
            "";
          if (teacherName !== fTeacher) return false;
        }

        if (fromDate && due && dayjs(due).isBefore(dayjs(fromDate).startOf("day"))) return false;
        if (toDate && due && dayjs(due).isAfter(dayjs(toDate).endOf("day"))) return false;

        if (q.trim()) {
          const hay = [
            a.title, a.subject, a.description, a.instructions,
            a.className, a.section,
            ...(Array.isArray(a.sections) ? a.sections : []),
            a.branch,
            ...(Array.isArray(a.branches) ? a.branches : []),
          ].map((x) => String(x || "").toLowerCase()).join(" ");
          if (!hay.includes(q.trim().toLowerCase())) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const da = toJS(a.dueAt) || new Date(8640000000000000);
        const dbb = toJS(b.dueAt) || new Date(8640000000000000);
        if (da.getTime() !== dbb.getTime()) return da - dbb;
        return (toJS(b.givenAt || b.createdAt) - toJS(a.givenAt || a.createdAt));
      });
  }, [items, profile, onlyPending, fStatus, fSubject, fTeacher, fromDate, toDate, q, people, submissions]);

  const classTag = useMemo(
    () => [profile?.className, profile?.section].filter(Boolean).join("-") || "—",
    [profile?.className, profile?.section]
  );

  const onOpen = (url) => {
    if (!url) return toast.info("File/link not available.", { containerId: TOAST_CID });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openSubmitModal = (a) => {
    if (!uid) { toast.error("Not signed in. Please login again.", { containerId: TOAST_CID }); return; }
    setModalAssignment(a);
    setModalOpen(true);
  };

  const handleSubmit = async ({ file, status }) => {
    if (!modalAssignment || !schoolId || !uid) {
      toast.error("Not signed in or missing context.", { containerId: TOAST_CID });
      return;
    }
    const a = modalAssignment;
    const subDocRef = doc(db, "schools", schoolId, "assignments", a.id, "submissions", uid);

    let fileURL = submissions[a.id]?.fileURL || "";
    let fileName = submissions[a.id]?.fileName || "";

    if (file) {
      const safeName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const path = `schools/${schoolId}/assignments/${a.id}/submissions/${uid}/${safeName}`;
      const fileRef = sRef(storage, path);
      await uploadBytes(fileRef, file);
      fileURL = await getDownloadURL(fileRef);
      fileName = file.name;
    }

    const normalizedStatus = (status || "completed").toLowerCase();

    const payload = {
      uid,
      studentId,
      className: profile?.className || "",
      section: profile?.section || "",
      branch: profile?.branch || "",
      byStudent: {
        name: profile?.name || profile?.displayName || studentName || "",
        rollNo: profile?.rollNo || "",
      },
      status: normalizedStatus,
      submittedAt: (normalizedStatus === "completed" || !!file) ? serverTimestamp() : null,
      fileURL: fileURL || "",
      fileName: fileName || "",
    };

    await setDoc(subDocRef, payload, { merge: true });
    toast.success("Submission saved.", { containerId: TOAST_CID });
    setModalOpen(false);
  };

  const statusBadge = (a) => {
    const { overdue, dueSoon, submitted, graded, upcoming, pending } = classify(a);
    if (graded)  return <Badge bg="success">Graded</Badge>;
    if (pending) return <Badge bg="secondary">Pending</Badge>;
    if (submitted) return <Badge bg="secondary">Submitted</Badge>;
    if (overdue)  return <Badge bg="danger">Overdue</Badge>;
    if (dueSoon)  return <Badge bg="warning" text="dark">Due soon</Badge>;
    if (upcoming) return <Badge bg="info" text="dark">Upcoming</Badge>;
    return <Badge bg="primary">Assigned</Badge>;
  };

  const MarksChip = ({ sub }) => {
    if (!sub) return null;
    const hasScore = typeof sub.score === "number";
    const max = typeof sub.maxScore === "number" ? sub.maxScore : null;
    const pct = hasScore && max ? Math.round((sub.score / (max || 1)) * 100) : null;

    if (!hasScore && !sub.gradedAt && !sub.remarks) return null;

    return (
      <div className="mt-2 small">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {hasScore ? (
            <Badge bg="success">
              Marks: {sub.score}{max != null ? ` / ${max}` : ""}{pct != null ? ` (${pct}%)` : ""}
            </Badge>
          ) : (
            <Badge bg="success">Graded</Badge>
          )}
          {sub.gradedAt ? <span className="text-muted">• Graded: <b>{fmtAt(sub.gradedAt)}</b></span> : null}
        </div>
        {sub.remarks ? (
          <div className="text-muted mt-1">Remarks: <span className="text-light">{sub.remarks}</span></div>
        ) : null}
      </div>
    );
  };

  /* ---------------- Virtualized Grid ---------------- */
  const gridRef = useRef(null);

  const renderCard = (a) => {
    const given = toJS(a.givenAt || a.createdAt);
    const due = toJS(a.dueAt);
    const { overdue, dueSoon } = classify(a);

    const tKey = a.teacherId ? String(a.teacherId).trim() : "";
    const teacher = tKey ? people[tKey] : null;
    const teacherName =
      teacher?.displayName ||
      teacher?.name ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
      teacher?.fullName ||
      a.teacherName ||
      (typeof a.teacher === "string" ? a.teacher : "") ||
      "—";

    const subj = a.subject || a.title || "Assignment";
    const ico = subjectIcon(subj);

    const canOpen = !!(a.fileURL || a.link || a.attachmentUrl);
    const mySub = submissions[a.id];

    const hasScore = mySub && typeof mySub.score === "number";
    const maxScore = mySub && typeof mySub.maxScore === "number" ? mySub.maxScore : null;
    const pct = hasScore && maxScore ? Math.round((mySub.score / (maxScore || 1)) * 100) : null;

    const secTxt = a.section ? `-${a.section}` : (Array.isArray(a.sections) && a.sections.length ? `-${a.sections.join(",")}` : "");
    const branchTxt = a.branch ? ` • ${a.branch}` : (Array.isArray(a.branches) && a.branches.length ? ` • ${a.branches.join(",")}` : "");

    return (
      <Card
        className={`h-100 shadow-sm border-0 assignment-card ${overdue ? "overdue" : dueSoon ? "due-soon" : "assigned"}`}
        style={{ borderColor: hexToRgba(primaryColor, 0.22), position: "relative", overflow: "hidden" }}
      >
        <div className="card-particles" aria-hidden />

        <Card.Body className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="d-flex align-items-center gap-3">
              <div className="subj-ico d-inline-flex align-items-center justify-content-center" title={subj}>
                <i className={`bi ${ico}`} />
              </div>
              <div className="fw-semibold text-truncate" title={a.title || subj}>
                {a.title || subj}
              </div>
            </div>
            {statusBadge(a)}
          </div>

          <div className="text-muted small mb-1">
            Subject: <span className="fw-semibold">{subj}</span>
          </div>

          <div className="small d-flex align-items-center gap-2">
            <i className="bi bi-person-badge" />
            <span className="text-muted">Teacher:</span>
            <span className="fw-semibold text-truncate">{teacherName}</span>
          </div>

          <div className="text-muted small mt-2">
            Given: <span className="fw-semibold">{fmtDate(given)}</span>
            {due ? <> &nbsp;• Due: <span className="fw-semibold">{fmtDate(due)}</span></> : null}
          </div>

          {a.description || a.instructions ? (
            <div className="small mt-2 text-truncate" title={(a.description||a.instructions)}>
              {(a.description || a.instructions)}
            </div>
          ) : null}

          {mySub ? (
            <>
              <div className="small mt-2">
                <div className="text-muted">
                  Your status: <b>{(mySub.status || "").toUpperCase()}</b>
                  {mySub.submittedAt ? <> • Submitted: <b>{fmtAt(mySub.submittedAt)}</b></> : null}
                </div>
                {mySub.fileURL ? (
                  <div className="mt-1">
                    <Button size="sm" variant="outline-success" onClick={() => onOpen(mySub.fileURL)}>
                      <i className="bi bi-box-arrow-up-right me-1" />
                      Open your file{mySub.fileName ? ` (${mySub.fileName})` : ""}
                    </Button>
                  </div>
                ) : null}
              </div>
              <MarksChip sub={mySub} />
            </>
          ) : (
            <div className="small mt-2 text-muted">No submission yet.</div>
          )}

          {hasScore ? (
            <div className="mt-2">
              <Badge bg="success" className="me-1">
                {mySub.score}{maxScore != null ? ` / ${maxScore}` : ""}{pct != null ? ` (${pct}%)` : ""}
              </Badge>
            </div>
          ) : null}

          <div className="mt-auto pt-3 d-flex justify-content-between align-items-center">
            <Badge bg="light" text="dark" className="border">
              {(a.className || "—")}{secTxt}{branchTxt}
            </Badge>

            <div className="d-flex gap-2">
              <Button
                size="sm"
                className={`btn-outline-accent ${!canOpen ? "disabled" : ""}`}
                disabled={!canOpen}
                onClick={() => onOpen(a.fileURL || a.link || a.attachmentUrl)}
              >
                <i className="bi bi-file-earmark-text me-1" />
                View
              </Button>
              <Button
                size="sm"
                className="btn-neo"
                onClick={() => openSubmitModal(a)}
              >
                <i className="bi bi-upload me-1" />
                {mySub ? "Update" : "Submit"}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const VirtualizedGrid = ({ data }) => (
    <div style={{ height: "70vh" }}>
      <AutoSizer>
        {({ width, height }) => {
          const gutter = 12;
          const cols = width < 576 ? 1 : width < 992 ? 2 : 3;
          const colWidth = Math.floor((width - gutter * (cols + 1)) / cols);
          const rowHeight = 325;
          const rowCount = Math.ceil(data.length / cols);

          return (
            <Grid
              ref={gridRef}
              columnCount={cols}
              columnWidth={colWidth + gutter}
              height={height}
              rowCount={rowCount}
              rowHeight={rowHeight + gutter}
              width={width}
              overscanRowCount={4}
            >
              {({ columnIndex, rowIndex, style }) => {
                const index = rowIndex * cols + columnIndex;
                if (index >= data.length) return <div style={style} />;
                const a = data[index];
                return (
                  <div style={{
                    ...style,
                    left: style.left + gutter,
                    top: style.top + gutter,
                    width: colWidth,
                    height: rowHeight,
                  }}>
                    {renderCard(a)}
                  </div>
                );
              }}
            </Grid>
          );
        }}
      </AutoSizer>
    </div>
  );

  return (
    <div className="position-relative overflow-hidden">
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primaryColor, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(accentComputed,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="student-assignments-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 60, density: { enable: true, area: 900 } },
            color: { value: [primaryColor, accentComputed] },
            links: { enable: true, color: hexToRgba(primaryColor, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.0, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 36 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 20 } } } },
          ],
        }}
      />

      <div className="p-2 p-sm-3 position-relative" style={{ zIndex: 2 }}>
        <ToastContainer
          containerId={TOAST_CID}
          position="bottom-right"
          newestOnTop
          closeOnClick={false}
          pauseOnFocusLoss
          draggable
          theme="dark"
        />

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="d-flex flex-column">
            <h5 className="mb-0">Assignments</h5>
            <small className="text-muted">
              {studentName || "Student"} • Class {[profile?.className, profile?.section].filter(Boolean).join("-") || "—"}
              {profile?.branch ? ` • ${profile.branch}` : ""}
              {isActive ? "" : " • Inactive"}
            </small>
          </div>

          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="switch"
              id="pending-switch"
              label="Only pending"
              checked={onlyPending}
              onChange={(e) => setOnlyPending(e.target.checked)}
              disabled={!isActive}
            />
            <AccentDropdown active={primaryColor} onPick={pickTheme} hexToRgba={hexToRgba} />
          </div>
        </div>

        {isActive && (
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="py-3">
              <Row className="g-2">
                <Col xs={12} md={4} lg={3}>
                  <InputGroup>
                    <InputGroup.Text className="bg-light"><i className="bi bi-search" /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search title / subject / instructions"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                    {subjectOptions.map((s) => (
                      <option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fTeacher} onChange={(e) => setFTeacher(e.target.value)}>
                    {teacherOptions.map((t) => (
                      <option key={t} value={t}>{t === "all" ? "All teachers" : t}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="dueSoon">Due soon (≤48h)</option>
                    <option value="overdue">Overdue</option>
                    <option value="submitted">Submitted</option>
                    <option value="graded">Graded</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={1}>
                  <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="Due from" />
                </Col>
                <Col xs={6} md={4} lg={1}>
                  <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} title="Due to" />
                </Col>

                <Col xs={12} md={3} lg={1} className="d-grid">
                  <Button
                    className="btn-ghost"
                    onClick={() => { setQ(""); setFSubject("all"); setFTeacher("all"); setFStatus("all"); setFromDate(""); setToDate(""); }}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!loadingProfile && !isActive ? (
          <Alert variant="warning" className="mb-3">
            Your student account is currently <b>inactive</b>. Please contact your school
            management to activate your account to access assignments.
          </Alert>
        ) : null}

        <Card className="border-0 shadow-sm">
          <Card.Body>
            {loadingProfile ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
            ) : !isActive ? (
              <div className="text-muted py-4 text-center">Assignments are hidden until your account is activated.</div>
            ) : loadingAssignments ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading assignments…</div>
            ) : filtered.length === 0 ? (
              <div className="text-muted py-4 text-center">No assignments found.</div>
            ) : (
              <VirtualizedGrid data={filtered} />
            )}
          </Card.Body>
        </Card>
      </div>

      <style>{`
        .Toastify__toast {
          border-radius: 10px;
          font-size: 0.9rem;
          border: 1px solid ${hexToRgba(accentComputed, 0.4)};
          background: linear-gradient(135deg, ${hexToRgba(primaryColor, 0.14)} 0%, ${hexToRgba(accentComputed, 0.14)} 100%);
          backdrop-filter: blur(6px);
        }
        .Toastify__toast-body { color: ${accentComputed}; font-weight: 500; }
        .Toastify__progress-bar { background: ${accentComputed}; }

        .assignment-card {
          border-radius: 18px;
          background: linear-gradient(180deg, ${hexToRgba(primaryColor, 0.06)} 0%, transparent 60%);
          transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease, filter .18s ease, border-color .18s ease;
        }
        .assignment-card:hover { transform: translateY(-2px); box-shadow: 0 14px 32px ${hexToRgba(primaryColor, .14)}; border-color: ${hexToRgba(primaryColor, .28)}; }
        .assignment-card.overdue { box-shadow: 0 .35rem .9rem ${hexToRgba("#dc3545", .22)}; opacity: .92; }
        .assignment-card.due-soon { box-shadow: 0 .35rem .9rem ${hexToRgba("#ffc107", .22)}; }
        .assignment-card.submitted { filter: grayscale(.1); opacity: .9; }
        .assignment-card.graded { box-shadow: 0 .35rem .9rem ${hexToRgba("#28a745", .22)}; }

        .subj-ico {
          width: 64px; height: 64px; border-radius: 16px;
          background: ${hexToRgba(primaryColor, 0.18)};
          color: ${primaryColor};
          border: 1px solid ${hexToRgba(primaryColor, 0.30)};
          font-size: 34px;
        }
        @media (max-width: 576px) { .subj-ico { width: 54px; height: 54px; font-size: 28px; } }

        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(${hexToRgba(primaryColor, .18)} 1px, transparent 1px), radial-gradient(${hexToRgba(accentComputed, .18)} 1px, transparent 1px);
          background-position: 0 0, 12px 12px;
          background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite;
          opacity: .22;
        }
        .assignment-card.overdue .card-particles { animation-duration: 6s; opacity: .28; }
        .assignment-card.due-soon .card-particles { animation-duration: 7.5s; opacity: .26; }
        .assignment-card.submitted .card-particles { animation-duration: 12s; opacity: .18; }
        .assignment-card.graded .card-particles { animation-duration: 8s; opacity: .24; }
        @keyframes drift { 0% { background-position: 0 0, 12px 12px; } 50% { background-position: 12px 6px, 0 18px; } 100% { background-position: 0 0, 12px 12px; } }

        /* Neo primary action */
        .btn-neo{
          --_bg1: ${hexToRgba(primaryColor, .95)};
          --_bg2: ${hexToRgba(accentComputed, .95)};
          color:#fff !important;
          background: linear-gradient(135deg, var(--_bg1) 0%, var(--_bg2) 100%);
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          box-shadow: 0 10px 18px ${hexToRgba(primaryColor,.24)};
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
        }
        .btn-neo:hover{ transform: translateY(-1px); filter: brightness(1.04); box-shadow: 0 14px 26px ${hexToRgba(primaryColor,.30)}; }
        .btn-neo:active{ transform: translateY(0); filter: brightness(.98); box-shadow: 0 6px 14px ${hexToRgba(primaryColor,.24)} inset; }
        .btn-neo.disabled, .btn-neo:disabled { opacity: .65 !important; filter: grayscale(.2); box-shadow: none; }

        /* Ghost secondary */
        .btn-ghost{
          color: ${primaryColor} !important;
          background: transparent;
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease, color .12s ease;
        }
        .btn-ghost:hover{
          background: ${hexToRgba(primaryColor,.10)};
          box-shadow: 0 6px 16px ${hexToRgba(primaryColor,.18)};
          transform: translateY(-1px);
        }

        /* Outline accent */
        .btn-outline-accent {
          --accent: ${accentComputed};
          background-color: transparent !important;
          color: var(--accent) !important;
          border: 1px solid var(--accent) !important;
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease;
        }
        .btn-outline-accent:hover, .btn-outline-accent:focus {
          background-color: ${hexToRgba(accentComputed, .12)} !important;
          color: var(--accent) !important;
          box-shadow: 0 6px 16px ${hexToRgba(accentComputed,.18)};
          transform: translateY(-1px);
        }
      `}</style>

      <SubmitModal show={modalOpen} onHide={() => setModalOpen(false)} onSubmit={handleSubmit} initialStatus="completed" />
    </div>
  );
}
